import { DocumentType } from '~/constants/documentType';
import api from '~/services/api';
import type { IFile } from '~/services/modules/contract/types';
import type { TypeFile } from '~/pages/contract/step4/hooks/types';
import type { Cards as RenewalCard, Contract as RenewalQuote } from '~/pages/contract/renewal/interfaces/contract';
import type { Data as RenewalContract } from '~/pages/contract/renewal/interfaces/contractinfo';
import { getRenewalCodeType, type RenewalChannel } from '~/pages/contract/renewal/journey/renewalChannel';
import type { RenewalPayload, RenewalResult } from '~/pages/contract/renewal/journey/renewalPayment';

export type { RenewalCard, RenewalContract, RenewalQuote };

/** Organização Aride usada como padrão na renovação quando `org` não vem na URL. */
export const RENEWAL_DEFAULT_ORG_ID = 'rw0Om';

const onlyDigits = (value: string) => value.replace(/\D/g, '');

function v2() {
  if (!api.v2) {
    throw new Error('API v2 não está disponível');
  }
  return api.v2;
}

function unwrapData<T>(body: unknown): T {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

function withOrganization(path: string, organizationId: string) {
  const params = new URLSearchParams();
  params.set('organization_id', organizationId);
  return `${path}?${params.toString()}`;
}

/** `GET Contracts/{hash}` pode vir como `{ data: contrato }` ou com o contrato na raiz. */
export function normalizeRenewalContractBody(body: unknown): RenewalContract | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const record = body as Record<string, unknown>;
  const nested = record.data;
  if (nested && typeof nested === 'object' && 'contract_id' in nested) {
    return nested as RenewalContract;
  }
  if ('contract_id' in record) {
    return record as unknown as RenewalContract;
  }
  return undefined;
}

/** Contrato da renovação pelo hash do link. Antes da verificação, é a única chamada do fluxo. */
export async function getRenewalContract(hash: string, organizationId: string = RENEWAL_DEFAULT_ORG_ID) {
  const { data } = await v2().get(withOrganization(`Contracts/${hash}`, organizationId));
  const contract = normalizeRenewalContractBody(data);
  if (!contract) {
    throw new Error('Contrato não encontrado');
  }
  return contract;
}

/**
 * `POST auth/send-code` com o mesmo corpo do legado e da cotação (`document`, `codeType`).
 * Não registra o documento em log.
 */
export async function sendRenewalCode(document: string, channel: RenewalChannel) {
  const { data } = await v2().post('/auth/send-code', {
    document: onlyDigits(document),
    codeType: getRenewalCodeType(channel)
  });
  return data;
}

/** `POST auth/validate-code`. O resultado fica só em memória da página (não persiste entre recarregamentos). */
export async function validateRenewalCode(document: string, code: string, channel: RenewalChannel) {
  const { data } = await v2().post('/auth/validate-code', {
    document: onlyDigits(document),
    code,
    codeType: getRenewalCodeType(channel)
  });
  return unwrapData<unknown>(data);
}

/** Cotação da renovação (produtos, upsell, totais). Só é buscada depois de validar o código. */
export async function getRenewalQuote(hash: string, organizationId: string = RENEWAL_DEFAULT_ORG_ID) {
  const { data } = await v2().get(withOrganization(`Contracts/${hash}/quotes`, organizationId));
  return unwrapData<RenewalQuote>(data);
}

/** Cartões salvos do segurado (`insured.id` da cotação). */
export async function getRenewalCards(customerId: string, organizationId: string = RENEWAL_DEFAULT_ORG_ID) {
  const { data } = await v2().get(withOrganization(`Customers/${customerId}/cards`, organizationId));
  const cards = unwrapData<RenewalCard[] | null>(data);
  return Array.isArray(cards) ? cards : [];
}

export type RenewalCustomerUpdate = {
  name: string;
  email: string;
  mobile_phone: string;
  /** CPF e nascimento não são editáveis: vão como estão no contrato. */
  document: string;
  birthdate: string;
};

/**
 * `PUT Quotes/{quoteId}/customer` com o mesmo corpo do legado (`document_type` igual ao do legado).
 * Diferente do legado, o erro é propagado para o card mostrar e manter a edição aberta.
 */
export async function updateRenewalCustomer(quoteId: number, values: RenewalCustomerUpdate, organizationId: string) {
  const birthdate = values.birthdate ? new Date(values.birthdate) : null;
  const { data } = await v2().put(`/Quotes/${quoteId}/customer`, {
    name: values.name.trim(),
    email: values.email.trim(),
    mobile_phone: onlyDigits(values.mobile_phone),
    document: onlyDigits(values.document),
    document_type: DocumentType.NotaFiscaOuDeclaracaoDePropriedade,
    birthdate: birthdate && !Number.isNaN(birthdate.getTime()) ? birthdate.toISOString() : values.birthdate,
    organization_id: organizationId
  });
  return data;
}

export type RenewalAddressUpdate = {
  zipcode: string;
  street: string;
  number: string;
  neighborhood: string;
  additional_details: string;
  state: string;
  city: string;
};

/** `POST Contracts/{hash}/insured_items/address`: endereço onde o equipamento fica guardado. */
export async function updateRenewalAddress(hash: string, values: RenewalAddressUpdate, organizationId: string) {
  const { data } = await v2().post(`Contracts/${hash}/insured_items/address`, {
    zipcode: onlyDigits(values.zipcode),
    street: values.street.trim(),
    number: values.number.trim(),
    neighborhood: values.neighborhood.trim(),
    additional_details: values.additional_details.trim(),
    state: values.state,
    city: values.city.trim(),
    organization_id: organizationId
  });
  return data;
}

export type RenewalZipcodeAddress = Pick<RenewalAddressUpdate, 'street' | 'neighborhood' | 'city' | 'state'>;

/** `GET ZipCodes?zipcode=` (API v1, como no legado). Sem endereço na resposta, falha com «CEP não encontrado». */
export async function lookupRenewalZipcode(zipcode: string): Promise<RenewalZipcodeAddress> {
  const { data } = await api.get(`ZipCodes?zipcode=${onlyDigits(zipcode)}`);
  const found = unwrapData<Partial<RenewalZipcodeAddress> | null>(data);
  if (!found || !found.city) {
    throw new Error('CEP não encontrado');
  }
  return {
    street: found.street ?? '',
    neighborhood: found.neighborhood ?? '',
    city: found.city ?? '',
    state: found.state ?? ''
  };
}

export type RenewalQuoteTotals = {
  total: number;
  financial_charges: number;
  subtotal: number;
  installments: number;
  flat_rate?: number;
};

/** Recalcula a cotação com os produtos escolhidos (`PUT /Contracts/quotes/{quoteId}`, mesmo corpo do legado). */
export async function updateRenewalQuoteProducts(quoteId: number, productIds: number[], organizationId: string) {
  const { data } = await v2().put(`/Contracts/quotes/${quoteId}`, {
    products: productIds.map((productId) => ({ product_id: productId })),
    organization_id: organizationId
  });
  return unwrapData<RenewalQuoteTotals>(data);
}

/** Paga e conclui a renovação (`PUT /Contracts/quotes/{quoteId}/renew`), com `hash_card` ou `card_id`. */
export async function renewContract(quoteId: number, payload: RenewalPayload) {
  const { data } = await v2().put(`/Contracts/quotes/${quoteId}/renew`, payload);
  return unwrapData<RenewalResult>(data);
}

/** O que a etapa de fotos precisa do contrato novo para montar os campos (`getContractUploadFields`). */
export type RenewedContractUploadContext = {
  hasInvoice: boolean;
  isNew: boolean;
  productIds: number[];
  equipmentTypeId?: number;
};

type RenewedContractBody = {
  insured_item?: { has_invoice?: boolean; is_new?: boolean; equipment_type_id?: number };
  plan?: { products?: Array<{ product_id: number }> };
};

/**
 * Contrato criado pela renovação fora do prazo (`GET Contracts/{id}`, mesma chamada do legado `useRenewFlow`).
 * Só os dados que decidem quais fotos pedir.
 */
export async function getRenewedContract(contractId: string): Promise<RenewedContractUploadContext> {
  const { data } = await v2().get(`Contracts/${contractId}`);
  const contract = unwrapData<RenewedContractBody | undefined>(data);
  if (!contract?.insured_item) {
    throw new Error('Contrato renovado não encontrado');
  }
  return {
    hasInvoice: Boolean(contract.insured_item.has_invoice),
    isNew: Boolean(contract.insured_item.is_new),
    productIds: (contract.plan?.products ?? []).map((product) => product.product_id),
    equipmentTypeId: contract.insured_item.equipment_type_id
  };
}

/** Fotos do equipamento do contrato renovado (`POST contracts/{id}/insured_items/documents`, como no legado). */
export async function sendRenewalPhotos(
  contractId: string,
  files: Partial<Record<TypeFile, IFile>>,
  organizationId: string
) {
  const { data } = await v2().post(`/contracts/${contractId}/insured_items/documents`, {
    ...files,
    organization_id: organizationId
  });
  return data;
}
